import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
export { useMediaQuery } from "../../../lib/useMediaQuery";
import { IconClose } from "../../../app/icons";

/**
 * Zwevend menu bij een knop: blijft binnen het scherm, sluit met Esc of een klik ernaast,
 * pijltjes lopen door de knoppen.
 */
export function Popover({
  anchor,
  label,
  onClose,
  children,
}: {
  anchor: HTMLElement;
  label: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const b = anchor.getBoundingClientRect();
    const p = el.getBoundingClientRect();
    let top = b.bottom + 6;
    if (top + p.height > window.innerHeight - 12) top = Math.max(12, b.top - p.height - 6);
    setPos({ left: Math.max(12, Math.min(b.left, window.innerWidth - p.width - 12)), top });
    (el.querySelector<HTMLElement>("input, button") ?? el).focus();
  }, [anchor]);
  useEffect(() => {
    const down = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.contains(t)) onClose();
    };
    document.addEventListener("mousedown", down);
    return () => document.removeEventListener("mousedown", down);
  }, [anchor, onClose]);
  return (
    <div
      ref={ref}
      className="pl-pop"
      role="menu"
      aria-label={label}
      tabIndex={-1}
      style={pos ? { left: pos.left, top: pos.top } : { visibility: "hidden", left: 0, top: 0 }}
      onKeyDown={(e) => {
        const items = [...(ref.current?.querySelectorAll<HTMLElement>("button:not(:disabled)") ?? [])];
        const i = items.indexOf(document.activeElement as HTMLElement);
        if (e.key === "Escape") {
          e.preventDefault();
          onClose();
          anchor.focus();
        } else if (e.key === "ArrowDown" && items.length) {
          e.preventDefault();
          items[(i + 1) % items.length]!.focus();
        } else if (e.key === "ArrowUp" && items.length) {
          e.preventDefault();
          items[(i - 1 + items.length) % items.length]!.focus();
        }
      }}
    >
      {children}
    </div>
  );
}

/** Paneel rechts (telefoon: van onderen) met kop, inhoud en knoppen onderaan. */
export function Drawer({
  eyebrow,
  title,
  sub,
  onClose,
  footer,
  children,
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    (
      ref.current?.querySelector<HTMLElement>("[data-autofocus]") ?? ref.current?.querySelector<HTMLElement>("button")
    )?.focus();
    return () => prev?.focus?.();
  }, []);
  return (
    <>
      <div className="pl-scrim" onClick={onClose} />
      <aside
        ref={ref}
        className="pl-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pl-dtitle"
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <header className="pl-dhead">
          <div className="pl-eyebrow">{eyebrow}</div>
          <div className="pl-dtop">
            <h2 id="pl-dtitle">{title}</h2>
            <button type="button" className="pl-ico" aria-label="Sluiten" onClick={onClose}>
              <IconClose size={18} aria-hidden="true" />
            </button>
          </div>
          {sub}
        </header>
        <div className="pl-dbody">{children}</div>
        {footer && <footer className="pl-dfoot">{footer}</footer>}
      </aside>
    </>
  );
}
